import { ErrorHandler, InjectionToken, Injector, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { DevelopmentModule } from '@content-platform/web-angular-shared-util-development';
import { flatten } from 'lodash';
import { IpAddressService } from './logger/ip-address.service';
import { NewRelicService } from './logger/new-relic.service';
import { LoggerService } from './logger/logger.service';
import { ErrorHandlerService } from './logger/error-handler.service';
import { LoggingEnvironment } from './logging-environment.provider';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { PARAM_TRANSFORMERS } from './models';
import { USER_PROVIDED_META_REDUCERS } from '@ngrx/store';
import { LOG_ALL_STORE_ACTIONS_FLAG } from './logger/logger-config';
import { getActionLoggingReducer, StoreLoggerService } from './logger/store-logger.service';
import { MegaBeaconService } from './logger/mega-beacon.service';
import { GeolocationService } from './logger/geolocation.service';
import { NewRelicNewService } from './logger/new-relic-new.service';
import { Configuration } from '@content-platform/web-angular-shared-util-configuration';
import * as i0 from "@angular/core";
/**
 * This factory allows me to initiate the transformer classes, and provides around this issue:
 *   Function expressions are not supported in decorators
 * This is why I am passing in the un initiated classes through the Injection token below.
 * The factory is only provided in the forRoot, so that would have be called in the main app.
 */
export const INITIAL_TRANSFORMERS_CLASS = new InjectionToken('initial-transformer-class');
export function classFactory(injector, paramTransformers) {
    return flatten(paramTransformers)
        .filter(paramTransformer => !!paramTransformer)
        .map(paramTransformerClass => new paramTransformerClass(injector));
}
/**
 * Gets the meta reducers for the store:
 *  Adds the logging meta reducer
 *
 * @export
 * @param {StoreLoggerService} storeLogger
 * @returns {MetaReducer<{}>[]}
 */
export function getMetaReducers(storeLogger) {
    return [getActionLoggingReducer(storeLogger)];
}
/**
 * Constructs the environment object from Configuration options
 */
export function environmentFactory() {
    return {
        production: Configuration.isProductionEquivalentEnvironment(),
        version: Configuration.get('version')
    };
}
/**
 * Logging Module
 *
 * @export
 * @class LoggingModule
 */
export class LoggingModule {
    static forRoot(paramTransformer) {
        return {
            ngModule: LoggingModule,
            providers: [
                LoggerService,
                NewRelicService,
                StoreLoggerService,
                IpAddressService,
                MegaBeaconService,
                GeolocationService,
                NewRelicNewService,
                // https://discuss.newrelic.com/t/how-to-monitor-angular-5-application/53309/2
                { provide: ErrorHandler, useClass: ErrorHandlerService },
                { provide: LoggingEnvironment, useFactory: environmentFactory },
                { provide: INITIAL_TRANSFORMERS_CLASS, multi: true, useValue: paramTransformer },
                {
                    provide: PARAM_TRANSFORMERS,
                    useFactory: classFactory,
                    deps: [Injector, INITIAL_TRANSFORMERS_CLASS]
                },
                {
                    provide: USER_PROVIDED_META_REDUCERS,
                    deps: [StoreLoggerService],
                    useFactory: getMetaReducers
                }
            ]
        };
    }
    static withExtraParams(paramTransformer) {
        return {
            ngModule: LoggingModule,
            providers: [{ provide: INITIAL_TRANSFORMERS_CLASS, multi: true, useValue: paramTransformer }]
        };
    }
    static { this.ɵfac = function LoggingModule_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || LoggingModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: LoggingModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [provideHttpClient(withInterceptorsFromDi())], imports: [CommonModule,
            DevelopmentModule.withFlags([...LOG_ALL_STORE_ACTIONS_FLAG])] }); }
}
//# sourceMappingURL=logging.module.js.map